import * as React from 'react';
import { CgClose, CgMathPlus } from 'react-icons/cg';
import { AiOutlineStar } from 'react-icons/ai';
import SectionTitle from 'components/SectionTitle';
import Card from 'components/Card';
import MiniInput from 'components/MiniInput';
import { useWatchlist } from 'hooks/useWatchlist';

interface Props {
  onClose?: () => void;
  slug?: string;
}

const WatchList: React.FC<React.PropsWithChildren<Props>> = ({ onClose, slug = '' }) => {
  const [watchListTicker, setWatchListTicker] = React.useState('');
  const {
    isRemovingFromWatchlist,
    isUpdatingWatchlist,
    removeFromWatchlist,
    setTickerWatching,
    watchListData,
  } = useWatchlist();
  const items = (watchListData?.watch_list || []).filter((item) => !item.deletedAt);
  const activeItems = items
    .filter((item) => item.isWatching)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const inactiveItems = items
    .filter((item) => !item.isWatching)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  return (
    <Card className="h-full w-full overflow-hidden" isDark>
      <div className="mb-3 flex items-center justify-between sm:mb-4">
        <SectionTitle Icon={AiOutlineStar} title="Watchlist" style={{ marginBottom: 0 }} />
        {onClose && (
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded bg-transparent p-1 text-gray-400 transition-colors duration-200 hover:bg-palette-black-2 hover:text-white"
            aria-label="Close watchlist"
          >
            <CgClose size={16} />
          </button>
        )}
      </div>
      <div className="mb-3 sm:mb-4">
        <form
          className="w-full"
          onSubmit={async (e) => {
            e.preventDefault();

            if (!watchListTicker.trim()) {
              return;
            }

            // Keep what was typed if the write fails so it does not have to be retyped.
            if (await setTickerWatching(watchListTicker, true)) {
              setWatchListTicker('');
            }
          }}
        >
          <MiniInput
            placeholder="Ticker to watch..."
            value={watchListTicker}
            onChange={(e) => setWatchListTicker(e.target.value.toUpperCase())}
            reset={() => setWatchListTicker('')}
            button={
              <button
                disabled={isUpdatingWatchlist}
                className="border-border text-text-primary flex flex-shrink-0 items-center justify-center rounded-r-sm border-2 border-l border-brand-primary bg-brand-primary px-3 py-2 text-xs font-semibold transition-all duration-200 ease-in-out hover:border-white hover:bg-white hover:text-brand-primary disabled:border-brand-primary disabled:bg-brand-primary disabled:text-brand-primary sm:px-4"
                type="submit"
              >
                {isUpdatingWatchlist ? 'Loading...' : 'Add'}
              </button>
            }
          />
        </form>
      </div>
      <div className="flex justify-between"></div>
      <div className="flex h-full max-h-full flex-col overflow-hidden pb-16 sm:pb-20">
        <div className="flex-shrink flex-grow overflow-y-auto">
          {!!activeItems && activeItems.length > 0 && (
            <div className="mb-3 sm:mb-4">
              <div className="mb-1 text-xs font-semibold text-gray-400 sm:mb-2">Active Items</div>
              <div className="xs:grid-cols-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3">
                {activeItems.map((item) => (
                  <div
                    key={item.ticker}
                    className="flex items-center justify-between rounded bg-palette-black-4 p-1.5 pl-2 text-xs shadow-sm transition-shadow duration-200 hover:shadow-md sm:p-2 sm:pl-3"
                  >
                    <div className="min-w-0 flex-1">
                      <a
                        href={`${slug}/${item.ticker}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block truncate text-xs transition-colors"
                        title={item.ticker}
                      >
                        {item.ticker}
                      </a>
                    </div>
                    <button
                      className="ml-1 flex flex-shrink-0 items-center justify-center rounded bg-transparent p-0.5 transition-colors duration-200 hover:bg-palette-black-2 sm:p-1"
                      onClick={() => void setTickerWatching(item.ticker, false)}
                    >
                      <CgClose size={12} className="sm:h-4 sm:w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!!inactiveItems && inactiveItems.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold text-gray-400 sm:mb-2">Inactive Items</div>
              <div className="xs:grid-cols-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3">
                {inactiveItems.map((item) => (
                  <div
                    key={item.ticker}
                    className="group flex items-center justify-between rounded bg-palette-black-4 p-1.5 pl-2 text-xs shadow-sm transition-shadow duration-200 hover:shadow-md sm:p-2 sm:pl-3"
                  >
                    <div className="min-w-0 flex-1">
                      <a
                        href={`${slug}/${item.ticker}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block truncate text-xs transition-colors"
                        title={item.ticker}
                      >
                        {item.ticker}
                      </a>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-0.5 sm:gap-1">
                      <button
                        className="duration-250 p-0 text-[7px] font-medium uppercase text-put-text opacity-0 transition-opacity group-hover:opacity-100 sm:text-[8px]"
                        onClick={() => {
                          if (!isRemovingFromWatchlist) {
                            void removeFromWatchlist(item.id);
                          }
                        }}
                      >
                        Remove
                      </button>
                      <button
                        className="flex items-center justify-center rounded bg-transparent p-0.5 transition-colors duration-200 hover:bg-palette-black-2 sm:p-1"
                        onClick={() => void setTickerWatching(item.ticker, true)}
                      >
                        <CgMathPlus size={12} className="sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WatchList;
